import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TUser } from 'redux/reducers/user/types';
import { UserAvatarMenu } from 'ui/UserAvatarMenu';
import {
	getAttachmentTypes,
	getAttachmentTypesLoading,
	getGenerateUploadLinkLoading,
	getGenerateUploadLinkResult,
} from 'redux/reducers/attachments/selectors';
import {
	generateUploadLinkRequest,
	getAttachmentTypesRequest,
	resetGenerateUploadLink,
} from 'redux/reducers/attachments/reducer';
import { notificationContainer } from 'utils/notificationContainer';
import { TGenerateUploadLinkDialogProps, TOperatorRecipient } from './types';
import { collectOperatorsEmails, isValidEmail } from './utils';

export const GenerateUploadLinkDialog = ({
	open,
	onClose,
	emId,
}: TGenerateUploadLinkDialogProps) => {
	const dispatch = useDispatch();
	const types = useSelector(getAttachmentTypes);
	const loadingTypes = useSelector(getAttachmentTypesLoading);
	const generateLoading = useSelector(getGenerateUploadLinkLoading);
	const uploadLinkResult = useSelector(getGenerateUploadLinkResult);

	const [typeId, setTypeId] = useState<number | ''>('');
	const [comment, setComment] = useState('');
	const [operators, setOperators] = useState<TOperatorRecipient[]>([]);
	const [manualEmail, setManualEmail] = useState('');
	const [isCopied, setIsCopied] = useState(false);

	const operatorsEmails = useMemo(() => collectOperatorsEmails(operators), [operators]);

	const operatorsUi: TUser[] = useMemo(
		() =>
			operators.map(
				(operator) =>
					({
						id: operator.id,
						email: operator.email,
						fullName: operator.fullName,
					}) as TUser,
			),
		[operators],
	);

	const resetForm = () => {
		setTypeId('');
		setComment('');
		setOperators([]);
		setManualEmail('');
		setIsCopied(false);
	};

	const handleClose = () => {
		if (generateLoading) return;
		dispatch(resetGenerateUploadLink());
		resetForm();
		onClose();
	};

	const handleAddManualEmail = () => {
		const normalizedEmail = manualEmail.trim();
		if (!normalizedEmail) return;

		if (!isValidEmail(normalizedEmail)) {
			notificationContainer('Please enter a valid email address', 'error');
			return;
		}

		if (operatorsEmails.includes(normalizedEmail)) {
			notificationContainer('This email has already been added', 'warning');
			return;
		}

		setOperators((current) => [
			...current,
			{
				id: `manual-${normalizedEmail}`,
				email: normalizedEmail,
				fullName: normalizedEmail,
			},
		]);
		setManualEmail('');
	};

	const handleGenerate = () => {
		if (!typeId || operatorsEmails.length === 0) return;

		dispatch(
			generateUploadLinkRequest({
				emId,
				typeId: Number(typeId),
				operatorsEmails,
				comment: comment.trim() ? comment.trim() : undefined,
			}),
		);
	};

	const handleCopyLink = () => {
		const link = uploadLinkResult?.publicUploadUrl;
		if (!link) return;

		navigator.clipboard
			.writeText(link)
			.then(() => {
				setIsCopied(true);
				notificationContainer('Link copied to clipboard', 'success');
				setTimeout(() => setIsCopied(false), 1500);
			})
			.catch(() => {
				notificationContainer('Failed to copy link', 'error');
			});
	};

	useEffect(() => {
		if (open) {
			dispatch(getAttachmentTypesRequest());
		}
	}, [dispatch, open]);

	const isGenerateDisabled =
		generateLoading || !typeId || operatorsEmails.length === 0 || !!uploadLinkResult;

	return (
		<Dialog
			open={open}
			onClose={generateLoading ? () => {} : handleClose}
			fullWidth
			maxWidth="sm"
			PaperProps={{
				sx: { borderRadius: '12px' },
			}}
		>
			<DialogTitle color="primary">Generate link for uploading file</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					{uploadLinkResult?.publicUploadUrl ? (
						<>
							<Alert variant="filled" severity="success">
								Upload link has been generated successfully.
							</Alert>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<TextField
									label="Upload link"
									value={uploadLinkResult.publicUploadUrl}
									fullWidth
									size="small"
									InputProps={{
										readOnly: true,
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													color="primary"
													onClick={handleCopyLink}
													edge="end"
													aria-label="Copy upload link"
												>
													{isCopied ? (
														<CircularProgress size={18} />
													) : (
														<ContentCopy fontSize="small" />
													)}
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
							</Box>
							{uploadLinkResult.status && (
								<Typography variant="body2" color="text.secondary">
									Status: {uploadLinkResult.status}
								</Typography>
							)}
						</>
					) : (
						<>
							<TextField
								select
								label="Attachment type"
								value={typeId}
								onChange={(e) =>
									setTypeId(e.target.value === '' ? '' : Number(e.target.value))
								}
								disabled={loadingTypes || generateLoading}
								required
								size="small"
								fullWidth
							>
								<MenuItem value="" disabled>
									Select type
								</MenuItem>
								{types
									?.filter((type) => !type.isHidden)
									.map((type) => (
										<MenuItem key={type.id} value={type.id}>
											{type.type}
										</MenuItem>
									)) ?? null}
							</TextField>

							<TextField
								label="Comment (optional)"
								value={comment}
								onChange={(e) => setComment(e.target.value)}
								disabled={generateLoading}
								size="small"
								multiline
								minRows={2}
								maxRows={4}
								fullWidth
							/>

							<Box>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
									Notify operators by email
								</Typography>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										flexWrap: 'wrap',
										gap: 1,
										alignItems: 'center',
									}}
								>
									{operatorsUi.map((user) => (
										<UserAvatarMenu
											key={user.id}
											user={user}
											size="sm"
											onClickUser={(newUser) => {
												if (!newUser) return;
												setOperators((current) =>
													current.map((operator) =>
														operator.id === user.id
															? {
																	id: String(newUser.id),
																	email: newUser.email,
																	fullName: newUser.fullName,
																}
															: operator,
													),
												);
											}}
											delateButton
											onDeleteUser={() => {
												setOperators((current) =>
													current.filter((operator) => operator.id !== user.id),
												);
											}}
										/>
									))}
									<Button
										size="small"
										color="primary"
										sx={{
											fontWeight: 550,
											fontSize: '0.9rem',
											padding: '2px 10px',
											textTransform: 'none',
											borderRadius: '12px',
											border: '1px dashed #016193',
										}}
										disabled={generateLoading}
										onClick={() => {
											setOperators((current) => [
												...current,
												{ id: 'new-user', fullName: 'New User', email: '' },
											]);
										}}
									>
										Add User +
									</Button>
								</Box>
							</Box>

							<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
								<TextField
									label="Email"
									value={manualEmail}
									onChange={(e) => setManualEmail(e.target.value)}
									disabled={generateLoading}
									size="small"
									fullWidth
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault();
											handleAddManualEmail();
										}
									}}
								/>
								<Button
									variant="outlined"
									color="primary"
									onClick={handleAddManualEmail}
									disabled={generateLoading || !manualEmail.trim()}
									sx={{ mt: '2px', whiteSpace: 'nowrap' }}
								>
									Add email
								</Button>
							</Box>

							{operatorsEmails.length === 0 && (
								<Alert variant="filled" severity="info">
									Add at least one operator email to generate the upload link.
								</Alert>
							)}
						</>
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose} disabled={generateLoading}>
					{uploadLinkResult ? 'Close' : 'Cancel'}
				</Button>
				{!uploadLinkResult && (
					<Button
						variant="contained"
						color="primary"
						disabled={isGenerateDisabled}
						onClick={handleGenerate}
					>
						{generateLoading ? <CircularProgress size={22} color="inherit" /> : 'Generate'}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
